import { showSuccess } from '@calendly/alert'
import { httpRequest, parseQuery, extensionSafeRedirect } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import moment from 'moment-timezone'
import { nanoid } from 'nanoid'

import { getUser } from 'common'
import { Segment } from 'core/analytics/segment'
import { isAvailableFeatureSelector } from 'features/billing'
import {
  trackScheduleInRealTimeEvent,
  trackInviteeBookedV3,
  trackCRMInviteeBookedEvent,
  trackCRMFlowStepCompleted
} from 'features/contacts'
import { calculateDurationInMinutes, removeConflictingSpots, shiftSpotsDuration } from 'features/one_off_modal'
import { trackEvent } from 'hooks/analytics_events'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { rootApi } from 'root-api'
import { currentOrganizationSelector } from 'selectors/current_organization'
import {
  idSelector,
  timeNotationSelector,
  uuidSelector,
} from 'selectors/current_user'
import { generateTelemetryId } from 'services/stytch_script'

import {
  buildPublisherRescheduleEventGuestsPayload,
  buildPublisherRescheduleInviteePutBody,
  mapGuestRowsToEventGuestsPayload,
} from './build_publisher_reschedule_put_payload'
import { BOOK_MODAL_RESCHEDULE_COMPLETED_EVENT, DEFAULT_DURATION_OPTIONS } from './constants'
import { mergeInviteeAnswersIntoQuestionFormValues } from './reschedule_merge_questions'
import { setPromoTypeShown } from '../extension_promotion'
import {
  availabilityPreviewAttributesSelector,
  eventTypeDurationSelector,
  eventTypeIdSelector,
  eventTypeSelector,
  eventTypeUuidSelector,
  fetchedEventsStartRangeSelector,
  guestsSelector,
  hasCalendarErrorSelector,
  inviteeQuestionsBookingSelector,
  inviteeSelectedTrackedSelector,
  loadingEventTypeSelector,
  pendingTrackInviteeSelectedSelector,
  primaryInviteeSelector,
  rescheduleInviteeUuidSelector,
  schedulingLinkUuidSelector,
  selectedMembersSelector,
  selectedSpotSelector,
  sourceSelector,
  stytchTelemetryIdSelector,
  stytchTelemetryTimestampSelector,
  contactsBookingFlowSelector,
  calendarStepSelector,
  bookingTypeSelector,
  showRecaptchaChallengeSelector,
  recaptchaV2TokenSelector,
} from './selectors'
import { syncPageRenderedAtMetaForHttpTools } from './sync_page_rendered_at_meta_for_http_tools'
import { normalizeSelectedCalendarDay } from './utils/normalize_selected_calendar_day'

export const BookErrorMessages = {
  requiredInvitee: 'Add an invitee to book the meeting',
  allowedUpTo30hosts: 'Allowed only up to 30 hosts',
}

export const STEPS = {
  eventType: 1,
  calendar: 2,
}

export const MOBILE_STEPS = {
  date: 'date',
  time: 'time',
  form: 'form',
}

const MOBILE_STEP_INITIAL = MOBILE_STEPS.date

export const previousMobileStep = (step) => {
  if (step === MOBILE_STEPS.time) return MOBILE_STEPS.date
  if (step === MOBILE_STEPS.form) return MOBILE_STEPS.time

  return null
}

export const nextMobileStep = (step) => {
  if (step === MOBILE_STEPS.date) return MOBILE_STEPS.time
  if (step === MOBILE_STEPS.time) return MOBILE_STEPS.form

  return null
}

const fetchedEventsDaysRange = 7
const maxHosts = 30

const emptyInvitee = {
  name: '',
  email: '',
  timezone: '',
  phone: '',
  primary: true,
  selected: true,
  preFilled: false,
}

const initialColorIndexes = [0, 1, 2, 3, 4, 5, 6, 7, 8]

// Returns the booked meeting duration in minutes derived from ISO timestamps.
// Falls back to the top-level `duration` field if timestamps are absent/invalid.
const scheduledEventDurationMinutes = (scheduledEvent) => {
  const start = scheduledEvent?.start_time
  const end = scheduledEvent?.end_time
  if (start && end) {
    const diffMs = new Date(end) - new Date(start)
    const mins = Math.round(diffMs / 60000)
    if (mins > 0 && mins <= 1440) return mins
  }
  const raw = scheduledEvent?.duration
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

// Returns true if scheduledEvent has at least something useful to patch (name or computable duration).
const hasScheduledEventData = (scheduledEvent) => (
  !!scheduledEvent?.name || scheduledEventDurationMinutes(scheduledEvent) != null
)

const applyScheduledEventNameDuration = (eventType, scheduledEvent, durationField) => {
  const hasName = !!scheduledEvent?.name
  const bookedDuration = scheduledEventDurationMinutes(scheduledEvent)

  // Nothing actionable — return originals unchanged.
  if (!hasName && bookedDuration == null) return { eventType, durationField }

  const patched = {
    ...eventType,
    ...(hasName && { name: scheduledEvent.name }),
    ...(bookedDuration != null && { duration: bookedDuration }),
  }
  const effectiveDuration = bookedDuration ?? eventType.duration
  const isCustomDuration = !DEFAULT_DURATION_OPTIONS.includes(effectiveDuration)
  const patchedDurationField = {
    ...durationField,
    duration: isCustomDuration ? 'custom' : effectiveDuration,
    customDurationValue: isCustomDuration ? effectiveDuration : '',
    customDurationUnit: isCustomDuration ? 'minutes' : durationField.customDurationUnit,
  }
  return { eventType: patched, durationField: patchedDurationField }
}

const durationFieldValuesChanged = (previousDurationField, nextDurationField) => (
  previousDurationField.duration !== nextDurationField.duration
  || previousDurationField.customDurationValue !== nextDurationField.customDurationValue
  || previousDurationField.customDurationUnit !== nextDurationField.customDurationUnit
)

const applyDurationFieldChange = (state, newDurationField, currentUser) => {
  const { timezone, hostsField } = state.calendarStep
  let { selectedSpot } = state.calendarStep

  if (
    durationFieldValuesChanged(state.calendarStep.durationField, newDurationField)
    && selectedSpot
  ) {
    const newDurationInMinutes = calculateDurationInMinutes(newDurationField)
    if (newDurationInMinutes && newDurationInMinutes > 0 && newDurationInMinutes <= 720) {
      const potentialSpots = shiftSpotsDuration([state.calendarStep.selectedSpot], newDurationInMinutes)
      const selectedSpots = removeConflictingSpots(
        potentialSpots,
        timezone,
        hostsField.selectedMembers,
        currentUser,
        false,
        false,
      )
      selectedSpot = (selectedSpots.length > 0) ? selectedSpots[0] : null
    }
  }

  return {
    ...state,
    calendarStep: {
      ...state.calendarStep,
      durationField: newDurationField,
      selectedSpot,
    },
  }
}

// Merges invitee answers from the booked meeting (scheduledEvent.invitee.event_fields)
// into the question form values derived from eventType.custom_fields.
// Returns the merged array ready for calendarStep.questionFormValues, or null if
// either data source is absent.
// API format for event_fields: [{ name: string, values: string[] }]
const applyScheduledEventQuestions = (eventType, scheduledEvent) => {
  const eventFields = scheduledEvent?.invitee?.event_fields
  if (!eventFields?.length || !eventType?.custom_fields?.length) return null

  const answersByName = {}
  eventFields.forEach(({ name, values }) => { answersByName[name] = values || [] })

  return eventType.custom_fields
    .filter((field) => field.enabled)
    .sort((a, b) => a.position - b.position)
    .map((field) => {
      const base = {
        answerChoices: field.answer_choices,
        includeOther: field.include_other,
        value: undefined,
        customValue: '',
        ...field,
      }
      const values = answersByName[field.name]
      if (!values || values.length === 0) return base
      // choices_many stores an array of selected values; everything else uses a single string.
      const value = field.format === 'choices_many' ? values : values[0]
      return { ...base, value }
    })
}

/** Prefer GraphQL invitee `eventFields` when present (publisher host reschedule); else scheduled snapshot. */
const resolveRescheduleQuestionFormValues = (eventType, state, scheduledEventForQuestions = state.scheduledEvent) => {
  const fromScheduled = applyScheduledEventQuestions(eventType, scheduledEventForQuestions)
  if (state.rescheduleInviteeEventFields?.length) {
    return mergeInviteeAnswersIntoQuestionFormValues(eventType, state.rescheduleInviteeEventFields)
  }
  return fromScheduled ?? state.calendarStep.questionFormValues
}

const initialState = {
  step: STEPS.calendar,
  mobileStep: MOBILE_STEP_INITIAL,
  bookingType: 'event_type',
  bookErrorMsg: '',
  stytchBlockContext: null,
  stytchTelemetryId: null,
  stytchTelemetryTimestamp: null,
  modalVisible: false,
  loadingEventType: true,
  loadingEventTypeMembers: true,
  loadingScheduledEvent: true,
  eventTypeId: null,
  eventTypeUuid: null,
  eventTypeType: null,
  eventTypeOwnerType: null,
  eventTypeOwnerId: null,
  eventType: {},
  scheduledEvent: {},
  rescheduleInviteeUuid: null,
  rescheduleEventUuid: null,
  rescheduleReason: '',
  rescheduleInviteeCharge: null,
  rescheduleFormerStartIso: null,
  rescheduleFormerEndIso: null,
  rescheduleTimezoneLabel: null,
  rescheduleInviteeEventFields: null,
  /** Snapshot of `event.event_guests` when opening publisher reschedule (BoB PUT contract). */
  rescheduleInitialEventGuestPayload: null,
  /** True after add/remove/update guest (or clear invitee) so we do not fall back to snapshot. */
  rescheduleGuestsFieldTouched: false,
  rescheduleSubmitting: false,
  schedulingLinkUuid: null,
  currentTime: moment(),
  invitees: [
    emptyInvitee,
  ],
  recentInvitees: {
    loaded: false,
    suggestions: [],
  },
  inviteeSelectedTracked: false,
  pendingTrackInviteeSelected: null,
  guests: [],
  calendarStep: {
    view: 'available_times',
    loadingCalendarData: true,
    dateRangeStart: moment(),
    timezone: null,
    rangeStart: null,
    rangeEnd: null,
    fetchedEventsStartRange: moment().startOf('week').format('YYYY-MM-DD'),
    fetchedEventsDaysRange,
    selectedSpot: null,
    sidebarFormValues: null,
    draftInvitee: {
      name: '',
      email: '',
      timezone: null,
      phone: '',
      nameError: '',
      emailError: '',
    },
    durationField: {
      duration: '30',
      customDurationValue: '',
      customDurationUnit: 'minutes',
    },
    hostsField: {
      colorIndexes: initialColorIndexes,
      selectedMembers: [],
    },
    questionFormValues: null,
  },
  calendarList: {
    loadingCalendarData: true,
    today: moment.tz(),
    monthShift: 0,
    spotAutofocus: false,
    calendar: {
      selectedDaySpots: []
    },
    calendars: {},
  },
  source: null,
  contactsBookingFlow: false,
  recaptcha: {
    showChallenge: false,
    v2Token: null,
  },
}

let setEventTypeAction = null
let setScheduledEventAction = null
let setPendingTrackInviteeSelectedAction = null
let setInviteeSelectedTrackedAction = null
let closeModalAction = null
let setTelemetryIdAction = null
let showRecaptchaChallengeAction = null

const newEventsMap = {
  'book_on_behalf.invitee_selected': TRACKING_EVENT_NAMES.FLOW_STEP_COMPLETED,
  'book_on_behalf.settings': TRACKING_EVENT_NAMES.FLOW_STEP_CLICKED,
  'book_on_behalf.success': TRACKING_EVENT_NAMES.FLOW_STEP_COMPLETED,
}

const trackBookModalEvent = (event, props, newProps) => (_dispatch, getState) => {
  const state = getState()
  const et = eventTypeSelector(state)
  const eventTypeUuid = eventTypeUuidSelector(state)
  const org = currentOrganizationSelector(state)
  const currentUserUuid = uuidSelector(state)

  Segment.track(event, {
    et_kind: et.kind,
    et_ownership: et.owner_type === 'User' ? 'personal' : 'other',
    et_pooling: et.pooling_type,
    event_type_id: eventTypeUuid,
    organization_id: org.id,
    organization_tier: org.tier,
    organization_stage: org.stage,
    ...props,
  })

  const newEventName = newEventsMap[event]

  if (newEventName) {
    trackEvent({
      name: newEventName,
      dependencies: {
        user_uuid: currentUserUuid
      },
      properties: {
        flow_name: 'schedule_in_real_time',
        flow_version: 'v1',
        ...newProps
      }
    })
  }
}

export const trackTelemetryId = createAsyncThunk(
  'bookModal/trackTelemetryId',
  async ({ source = 'unknown' } = {}, { dispatch }) => {
    const { telemetryId } = await generateTelemetryId({ source })
    if (telemetryId) {
      dispatch(setTelemetryIdAction({ telemetryId, timestamp: moment() }))
    }
  }
)

// Telemetry IDs expire after 5 minutes
const TELEMETRY_MAX_AGE_MS = 5 * 60 * 1000

/**
 * Ensures a fresh telemetry ID is available before booking.
 * Regenerates if missing or stale (>5 minutes old).
 */
const ensureTelemetryId = createAsyncThunk(
  'bookModal/ensureTelemetryId',
  async (_payload, { dispatch, getState }) => {
    const state = getState()
    const existingTelemetryId = stytchTelemetryIdSelector(state)
    const existingTimestamp = stytchTelemetryTimestampSelector(state)
    const source = sourceSelector(state) || 'book_on_behalf'

    // Check if existing telemetry ID is valid and not stale
    if (existingTelemetryId && existingTimestamp) {
      const ageMs = moment().diff(existingTimestamp, 'milliseconds')
      if (ageMs < TELEMETRY_MAX_AGE_MS) {
        return { telemetryId: existingTelemetryId, refreshed: false }
      }
      // ID is stale, clear it before attempting regeneration
      dispatch(setTelemetryIdAction({ telemetryId: null, timestamp: null }))
    }

    // Generate a new telemetry ID (either missing or stale)
    const { telemetryId } = await generateTelemetryId({ source })

    if (telemetryId) {
      dispatch(setTelemetryIdAction({ telemetryId, timestamp: moment() }))
      return { telemetryId, refreshed: true }
    }

    return { telemetryId: null, refreshed: false }
  }
)

const baseApiPath = ({
  eventTypeType,
  eventTypeOwnerType,
  eventTypeOwnerId,
}) => {
  if (eventTypeType === 'ManagedEventType') {
    return '/api/organization/managed_event_types'
  }

  if (eventTypeOwnerType === 'Organization') {
    return '/api/organization/event_types'
  }

  if (eventTypeOwnerType === 'Page') {
    return `/api/pages/${eventTypeOwnerId}/event_types`
  }

  return `/api/users/${eventTypeOwnerId || 'me'}/event_types`
}

const fetchMemberEventData = async (userId, getState, { uuid } = null) => {
  const rangeStart = fetchedEventsStartRangeSelector(getState())
  const rangeEnd = moment(rangeStart)
    .add(fetchedEventsDaysRange, 'days')
    .format('YYYY-MM-DD')

  const [success, eventData] = await httpRequest({
    url: '/api/user_busy_times',
    method: 'GET',
    params: {
      user_uuid: userId,
      start_time: rangeStart,
      end_time: rangeEnd,
      event_type_uuid: uuid
    },
  })

  return { userId, eventData: success ? eventData : null, calendarError: !success }
}

const fetchMemberAvailabilityRules = async (eventType, member, getState) => {
  const { data: { organization_membership: currentMembership } } = getUser.select()(getState())

  let rules = []

  if (eventType.own_availability_rule) {
    rules.push(eventType.own_availability_rule)
  }

  if (member.own_availability_rule) {
    rules.push(member.own_availability_rule)
  }

  if (
    currentMembership.user_id === member.user_id
    && currentMembership.availability_rules
  ) {
    rules = rules.concat(currentMembership.availability_rules)
  } else {
    const [success, res] = await httpRequest({
      url: `/api/users/${member.user_id}/availability/schedules`,
      method: 'GET',
    })
    if (success) {
      rules = rules.concat(res.rules)
    }
  }

  return rules
}

const fetchMemberData = async (eventType, member, getState) => {
  const [{ eventData, calendarError }, availabilityRules] = await Promise.all([
    fetchMemberEventData(member.user_uuid, getState, eventType),
    fetchMemberAvailabilityRules(eventType, member, getState),
  ])

  return { ...member, eventData, calendarError, availabilityRules }
}

export const trackEventAction = createAsyncThunk(
  'bookModal/trackEventAction',
  async (payload, { dispatch }) => {
    dispatch(trackBookModalEvent(payload.event, payload.attributes || {}, payload.newAttributes || {}))
    return payload
  }
)

export const setPrimaryInvitee = createAsyncThunk(
  'bookModal/setPrimaryInvitee',
  async (payload, { dispatch, getState }) => {
    const state = getState()
    const inviteeSelectedTracked = inviteeSelectedTrackedSelector(state)
    const loadingEventType = loadingEventTypeSelector(state)

    if (!inviteeSelectedTracked) {
      if (loadingEventType) {
        dispatch(
          setPendingTrackInviteeSelectedAction({
            timezoneSet: Boolean(payload.timezone),
            recentInvitee: Boolean(payload.preFilled)
          })
        )
      } else {
        dispatch(setInviteeSelectedTrackedAction())
        dispatch(
          trackBookModalEvent('book_on_behalf.invitee_selected', {
            recent_invitee: Boolean(payload.preFilled),
            timezone_set: Boolean(payload.timezone),
          }, {
            flow_step: 'invitee_selected',
            completion_method: 'step_completed',
          })
        )
      }
    }

    return payload
  }
)

export const fetchBookEventType = createAsyncThunk(
  'bookModal/fetchBookEventType',
  async (
    { eventTypeId, eventTypeUuid, eventTypeType, eventTypeOwnerType, eventTypeOwnerId },
    { dispatch, rejectWithValue, getState }
  ) => {
    const [success, eventType] = await httpRequest({
      method: 'GET',
      url: `${baseApiPath({
        eventTypeType,
        eventTypeOwnerType,
        eventTypeOwnerId,
      })}/${eventTypeId}`,
    })

    if (!success) return rejectWithValue(eventTypeId)

    const currentState = getState()
    const isMultipleDurationsAvailable = isAvailableFeatureSelector(currentState, { featureKey: 'multiple_durations' })
    dispatch(setEventTypeAction({ eventType, isMultipleDurationsAvailable }))

    const selectedMembers = await Promise.all(
      eventType.memberships
        .filter((member) => member.enabled && !member.is_pending)
        .map((member) => fetchMemberData(eventType, member, getState))
    )

    const state = getState()
    const currentUserId = idSelector(state)
    const inviteeSelectedTracked = inviteeSelectedTrackedSelector(state)
    const pendingTrackInviteeSelected = pendingTrackInviteeSelectedSelector(state)

    let selectedHostIndex = selectedMembers.findIndex(
      (m) => m.user_id === currentUserId
    )
    if (selectedHostIndex === -1) { selectedHostIndex = selectedMembers.findIndex((m) => m.primary) }
    if (selectedHostIndex === -1) selectedHostIndex = 0

    if (pendingTrackInviteeSelected && !inviteeSelectedTracked) {
      dispatch(setInviteeSelectedTrackedAction())
      dispatch(
        trackBookModalEvent('book_on_behalf.invitee_selected', {
          recent_invitee: Boolean(pendingTrackInviteeSelected.recentInvitee),
          timezone_set: pendingTrackInviteeSelected.timezoneSet,
        }, {
          flow_step: 'invitee_selected',
          completion_method: 'step_completed',
        })
      )
    }

    return {
      eventType,
      eventTypeUuid: eventTypeUuid ?? eventType.uuid,
      selectedMembers,
      selectedHostIndex,
    }
  }
)

export const fetchScheduledEventData = createAsyncThunk(
  'bookModal/fetchScheduledEventData',
  async (meetingUuid, { dispatch, getState }) => {
    const fetchByPeriod = async (period) => {
      const [eventsSuccess, eventsData] = await httpRequest({
        method: 'GET',
        url: '/api/scheduled_events/events',
        params: { uuid: meetingUuid, period },
      })
      const eventData = eventsData?.results?.length ? eventsData.results[0].events[0] : null
      return { eventsSuccess, eventData }
    }

    let { eventsSuccess, eventData } = await fetchByPeriod('past')
    if (!eventData) {
      const second = await fetchByPeriod('future')
      eventsSuccess = second.eventsSuccess
      eventData = second.eventData
    }

    let success = eventsSuccess

    if (success && eventData) {
      const [eventTypeSuccess, eventTypeData] = await httpRequest({
        method: 'GET',
        url: `${baseApiPath({
          eventTypeType: eventData.event_type.type,
          eventTypeOwnerType: eventData.event_type.owner_type,
          eventTypeOwnerId: eventData.event_type.owner_id,
        })}/${eventData.event_type.id}`,
      })

      success = eventTypeSuccess
      if (success) {
        eventData.event_type.memberships = eventTypeData.memberships
        eventData.is_zoom_configured = eventTypeData.is_zoom_configured
        eventData.is_gotomeeting_configured = eventTypeData.is_gotomeeting_configured
        eventData.google_conferencing_configured = eventTypeData.google_conferencing_configured
        eventData.is_microsoft_teams_configured = eventTypeData.is_microsoft_teams_configured
        eventData.is_webex_configured = eventTypeData.is_webex_configured
      }
    }

    const state = getState()
    const currentUserId = idSelector(state)

    dispatch(setScheduledEventAction(eventData))

    const selectedMembers = await Promise.all(
      eventData.event_type.memberships
        .filter((member) => member.enabled && !member.is_pending)
        .map((member) => fetchMemberData(eventData.event_type, member, getState))
    )
    let selectedHostIndex = selectedMembers.findIndex(
      (m) => m.user_id === currentUserId
    )
    if (selectedHostIndex === -1) { selectedHostIndex = selectedMembers.findIndex((m) => m.primary) }
    if (selectedHostIndex === -1) selectedHostIndex = 0
    return { scheduledEvent: success ? eventData : null, selectedMembers, selectedHostIndex }
  }
)

export const updateMembersEventData = createAsyncThunk(
  'bookModal/updateMembersEventData',
  async (members, { getState }) => {
    const state = getState()
    const eventType = eventTypeSelector(state)
    return Promise.all(members.map((m) => fetchMemberEventData(m.user_uuid, getState, eventType)))
  }
)

export const fetchEventTypeAvailability = createAsyncThunk(
  'bookModal/fetchEventTypeAvailability',
  async ({ month, timezone }, { getState, rejectWithValue }) => {
    const state = getState()
    const eventType = eventTypeSelector(state)

    let availability
    const schedulingLinkUuid = schedulingLinkUuidSelector(state)
    const rescheduleInviteeUuid = rescheduleInviteeUuidSelector(state)

    if (eventType.isCollective) {
      const eventTypeId = eventTypeIdSelector(state)
      const availabilityPreviewAttributes = availabilityPreviewAttributesSelector(state)

      if (availabilityPreviewAttributes.event_type_memberships_attributes?.length) {
        const [success, data] = await httpRequest({
          method: 'POST',
          url: `/api/event_types/${eventTypeId}/availability`,
          data: {
            origin: 'event_type_editor',
            date: month.startOf('month').format('YYYY-MM-DD'),
            timezone,
            diagnostics: false,
            availability_preview_attributes: availabilityPreviewAttributes,
            ...(schedulingLinkUuid && {
              scheduling_link_uuid: schedulingLinkUuid,
              rescheduling: {
                invitee_uuid: rescheduleInviteeUuid,
                is_publisher: true,
              },
            }),
          }
        })

        if (!success) return rejectWithValue(eventTypeId)
        availability = data
      } else {
        availability = { days: [] }
      }
    } else {
      const eventTypeUuid = eventTypeUuidSelector(state)

      const [success, data] = await httpRequest({
        type: 'GET',
        url: `/api/booking/event_types/${eventTypeUuid}/calendar/range`,
        params: {
          timezone,
          range_start: month.startOf('month').format('YYYY-MM-DD'),
          range_end: month.endOf('month').format('YYYY-MM-DD'),
          diagnostics: false,
          ...(schedulingLinkUuid && {
            scheduling_link_uuid: schedulingLinkUuid,
            rescheduling: {
              invitee_uuid: rescheduleInviteeUuid,
              is_publisher: true,
            },
          }),
        }
      })

      if (!success) return rejectWithValue(eventTypeUuid)
      availability = data
    }

    return { availability, month }
  }
)

export const removeHost = createAsyncThunk(
  'bookModal/removeHost',
  async (host, { dispatch }) => {
    dispatch(trackBookModalEvent('book_on_behalf.settings', { edit_hosts: 'removed' }, {
      flow_step: 'settings',
      completion_method: 'interaction',
    }))
    return host
  }
)

const getLocationConfig = (location) => {
  if (location.kind) {
    const locationConfig = {}
    const [kind] = location.kind.split('#')
    switch (kind) {
      case 'physical':
        locationConfig.kind = kind
        locationConfig.location = location[location.kind].location
        if (location[location.kind].details) {
          locationConfig.additional_info = location[location.kind].details
        }
        break
      case 'phone_call':
        locationConfig.kind = location.phoneCallKind
        if (location.phoneCallKind === 'inbound_call') {
          locationConfig.phone_number = location.hostPhoneNumber
          if (location.hostPhoneDetails) {
            locationConfig.additional_info = location.hostPhoneDetails
          }
        } else {
          locationConfig.phone_number = location.inviteePhoneNumber
        }
        break
      case 'custom':
        locationConfig.kind = kind
        locationConfig.location = location[location.kind].location
        break
      case 'google_conference':
      case 'zoom_conference':
      case 'microsoft_teams_conference':
      case 'webex_conference':
      case 'gotomeeting_conference':
        locationConfig.kind = kind
        break
      default:
        return null
    }
    return locationConfig
  }
  return null
}

/** API payloads must include `value` on each field; `undefined` is dropped by JSON.stringify. */
const eventFieldsForBookingApi = (fields) => (fields || []).map((f) => ({
  id: f.id,
  name: f.name,
  position: f.position,
  required: !!f.required,
  value: f.value == null ? '' : f.value,
}))

const buildStytchBlockContext = (telemetryId, hostBlock = false) => {
  const explanation = hostBlock
    ? I18n.t('publisher.booking_modal.stytch_proactive_block.host_block.explanation')
    : I18n.t('publisher.booking_modal.stytch_proactive_block.book_on_behalf.explanation')
  const message = hostBlock
    ? I18n.t('publisher.booking_modal.stytch_proactive_block.host_block.message')
    : I18n.t('publisher.booking_modal.stytch_proactive_block.book_on_behalf.message')

  return {
    telemetry_id: telemetryId || null,
    explanation,
    stytch_block_verdict: true,
    message,
    host_block: hostBlock
  }
}

const DEFAULT_BOOKING_ERROR_MESSAGE = 'That time cannot be booked, please choose another time'

const firstValidationMessageFromInviteeBlock = (inviteeBlock, { skipEmail } = {}) => {
  if (!inviteeBlock || typeof inviteeBlock !== 'object' || Array.isArray(inviteeBlock)) {
    return null
  }
  if (!skipEmail) {
    const emailErr = inviteeBlock.email?.[0]
    if (emailErr) return emailErr
  }
  for (const [key, value] of Object.entries(inviteeBlock)) {
    if (!skipEmail || key !== 'email') {
      if (Array.isArray(value) && typeof value[0] === 'string' && value[0]) {
        return value[0]
      }
    }
  }
  return null
}

const parseBookValidationError = (errorResponse) => {
  const baseErrorMessage = errorResponse.message || DEFAULT_BOOKING_ERROR_MESSAGE

  try {
    const bareInviteeMessage = firstValidationMessageFromInviteeBlock(errorResponse.invitee)
    if (bareInviteeMessage) {
      return { error: bareInviteeMessage, stytchBlockContext: null }
    }

    if (errorResponse.type !== 'validation' || !errorResponse.payload) {
      return { error: baseErrorMessage, stytchBlockContext: null }
    }

    const validationErrors = errorResponse.payload

    const inviteeEmailError = validationErrors.invitee?.email?.[0]
    if (inviteeEmailError) {
      return { error: inviteeEmailError, stytchBlockContext: null }
    }

    const inviteePayloadMessage = firstValidationMessageFromInviteeBlock(validationErrors.invitee, {
      skipEmail: true,
    })
    if (inviteePayloadMessage) {
      return { error: inviteePayloadMessage, stytchBlockContext: null }
    }

    const eventGuestsError = validationErrors.event?.event_guests?.[0]
    if (eventGuestsError) {
      return { error: eventGuestsError, stytchBlockContext: null }
    }

    // Check for Stytch moderation error (special case - returns block context)
    const failureDetails = validationErrors.failure?.json?.details
    if (failureDetails && Array.isArray(failureDetails)) {
      const stytchFailure = failureDetails.find((detail) => detail.code === 'stytch_moderation_failed')
      if (stytchFailure) {
        return {
          error: baseErrorMessage,
          stytchBlockContext: buildStytchBlockContext(
            stytchFailure.context?.telemetry_id,
            stytchFailure.context?.host_block === true
          )
        }
      }
      const recaptchaChallengeRequiredFailure = failureDetails.find((detail) => detail.code === 'recaptcha_challenge_required')
      if (recaptchaChallengeRequiredFailure) {
        return { error: null, stytchBlockContext: null, recaptchaChallengeRequired: true }
      }
    }
  } catch (error) {
    return { error: baseErrorMessage, stytchBlockContext: null }
  }

  return { error: baseErrorMessage, stytchBlockContext: null }
}

const startingStep = (source, inviteeEmail, inviteeName) => {
  let start = STEPS.calendar
  if (inviteeName && inviteeEmail) {
    start = STEPS.calendar
  }
  return start
}

const getInviteeTimezone = (payload, state) => payload.timezone // invitee's tz
  || state.calendarStep.hostsField.selectedMembers.find((m) => m.selectedHost)?.selectedTimezone // selected host's tz
  || state.calendarStep.timezone // tz spots are displayed in
  || ''

const trackAddAnInviteeError = (currentUserUuid) => {
  trackEvent({
    name: TRACKING_EVENT_NAMES.ERROR_STATE_SHOWN,
    dependencies: { user_uuid: currentUserUuid },
    properties: {
      error_name: 'add_an_invitee',
      location: 'book_on_behalf_modal'
    }
  })
}

export const bookAdhocEvent = createAsyncThunk(
  'bookModal/bookAdhocEvent',
  async (
    { name, location, description, onClose },
    { dispatch, getState, rejectWithValue }
  ) => {
    // Ensure fresh telemetry ID before booking
    await dispatch(ensureTelemetryId()).unwrap()

    const state = getState()
    const currentUserId = idSelector(state)
    const currentUserUuid = uuidSelector(state)
    const invitee = primaryInviteeSelector(state)
    const duration = eventTypeDurationSelector(state)
    const selectedSpot = selectedSpotSelector(state)
    const selectedMembers = selectedMembersSelector(state)
    const memberships = selectedMembers.map((m) => ({ user_uuid: m.user_uuid, location_organizer: !!m.location_organizer }))
    const guests = guestsSelector(state)
    const locationConfig = getLocationConfig(location)
    const stytchTelemetryId = stytchTelemetryIdSelector(state)
    const stytchTelemetryTimestamp = stytchTelemetryTimestampSelector(state)
    const hasCalendarError = hasCalendarErrorSelector(state)
    const showRecaptchaChallenge = showRecaptchaChallengeSelector(state)
    const recaptchaV2Token = recaptchaV2TokenSelector(state)

    if (hasCalendarError) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.errors.host_calendar_connection'),
      })
    }

    if (!invitee.email || !invitee.name) {
      trackAddAnInviteeError(currentUserUuid)
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.errors.required_invitee'),
      })
    }

    if (!selectedSpot) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.select_time_on_calendar'),
      })
    }

    const payload = {
      name,
      location,
      description: description?.html || '',
      duration,
      notify_invitees: false,
      booking_method: 'instant',
      selected_spots: [selectedSpot],
      memberships,
      reserve_times: false,
    }

    const request = httpRequest({
      url: '/api/users/me/adhoc_event_types',
      method: 'POST',
      data: { ...payload }
    })

    const [success, eventTypeData] = await request

    if (!success) return rejectWithValue({ error: eventTypeData.message })

    const bookOnBehalfData = {
      event_type_uuid: eventTypeData.uuid,
      event: {
        name,
        duration,
        description: description?.html || '',
        start_time: selectedSpot.start.format(),
        location_configuration: locationConfig,
        guests: guests.map((g) => ({ email: g.email })),
        memberships,
      },
      invitee: {
        full_name: invitee.name,
        email: invitee.email,
        timezone: invitee.timezone,
        time_notation: timeNotationSelector(state),
      },
      stytch_telemetry_id: stytchTelemetryId,
      stytch_telemetry_timestamp: stytchTelemetryTimestamp ? stytchTelemetryTimestamp.unix() : null,
      allow_override_events: true
    }
    if (showRecaptchaChallenge && recaptchaV2Token) {
      bookOnBehalfData.recaptcha_token_version_2 = recaptchaV2Token
    }

    syncPageRenderedAtMetaForHttpTools()
    const [successBook, bookData] = await httpRequest({
      method: 'POST',
      url: '/api/book_on_behalf',
      data: bookOnBehalfData
    })
    if (!successBook) {
      const parsed = parseBookValidationError(bookData)
      if (parsed.recaptchaChallengeRequired) {
        dispatch(showRecaptchaChallengeAction())
        return rejectWithValue(parsed)
      }
      return rejectWithValue(parsed)
    }

    if (invitee.contactUuid !== '') {
      trackScheduleInRealTimeEvent({
        contactUuid: invitee.contactUuid,
        step: 'book',
        source: null,
        track: (config) => {
          trackEvent({
            ...config,
            dependencies: { user_id: currentUserId }
          })
        }
      })
    }

    dispatch(rootApi.util.invalidateTags(['CONTACT']))
    dispatch(closeModalAction())
    dispatch(showSuccess(I18n.t('publisher.booking_modal.booking_success_message')))

    const { return_to: returnTo, closeRedirectUrl } = parseQuery(window.location.search)
    if (returnTo) {
      extensionSafeRedirect(returnTo || closeRedirectUrl, true, { id: bookData.event.uuid, success: true })
    } else if (onClose) {
      onClose()
    }

    return bookData
  }
)

export const bookEvent = createAsyncThunk(
  'bookModal/bookEvent',
  async (
    { name, location, description, onClose },
    { dispatch, getState, rejectWithValue }
  ) => {
    // Ensure fresh telemetry ID before booking
    await dispatch(ensureTelemetryId()).unwrap()

    const state = getState()
    const currentUserId = idSelector(state)
    const currentUserUuid = uuidSelector(state)
    const source = sourceSelector(state)
    const eventType = eventTypeSelector(state)
    const eventTypeUuid = eventTypeUuidSelector(state)
    const { view } = calendarStepSelector(state)
    const bookingType = bookingTypeSelector(state)
    const invitee = primaryInviteeSelector(state)
    const guests = guestsSelector(state)
    const selectedSpot = selectedSpotSelector(state)
    const duration = eventTypeDurationSelector(state)
    const locationConfig = getLocationConfig(location)
    const eventFields = inviteeQuestionsBookingSelector(state)
    const hasCalendarError = hasCalendarErrorSelector(state)
    const stytchTelemetryId = stytchTelemetryIdSelector(state)
    const stytchTelemetryTimestamp = stytchTelemetryTimestampSelector(state)
    const contactsBooking = contactsBookingFlowSelector(state)
    const showRecaptchaChallenge = showRecaptchaChallengeSelector(state)
    const recaptchaV2Token = recaptchaV2TokenSelector(state)

    if (hasCalendarError) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.errors.host_calendar_connection'),
      })
    }

    if (eventFields.some((field) => field.required && !field.value)) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.errors.required_questions'),
      })
    }

    if (!invitee.email || !invitee.name) {
      trackAddAnInviteeError(currentUserUuid)
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.errors.required_invitee'),
      })
    }

    if (!selectedSpot) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.select_time_on_calendar'),
      })
    }

    let memberships
    if (eventType.isCollective) {
      const selectedMembers = selectedMembersSelector(state)

      if (!selectedMembers.length) {
        return rejectWithValue({
          error: I18n.t('publisher.booking_modal.errors.required_host'),
        })
      }

      if (selectedMembers.length > maxHosts) {
        return rejectWithValue({
          error: I18n.t('publisher.booking_modal.errors.allowed_hosts'),
        })
      }

      memberships = selectedMembers.map((m) => ({ user_uuid: m.user_uuid }))
    }

    const bookEventGuestsPayload = mapGuestRowsToEventGuestsPayload(guests)

    const bookOnBehalfData = {
      event_type_uuid: eventTypeUuid,
      event: {
        name,
        duration,
        description: description?.html || '',
        start_time: selectedSpot.start.format(),
        location_configuration: locationConfig,
        guests: bookEventGuestsPayload,
        event_guests: bookEventGuestsPayload,
        memberships,
      },
      invitee: {
        full_name: invitee.name,
        email: invitee.email,
        timezone: invitee.timezone,
        time_notation: timeNotationSelector(state),
      },
      event_fields: eventFieldsForBookingApi(eventFields),
      stytch_telemetry_id: stytchTelemetryId,
      stytch_telemetry_timestamp: stytchTelemetryTimestamp ? stytchTelemetryTimestamp.unix() : null,
      allow_override_events: view === 'full_calendar'
    }
    if (showRecaptchaChallenge && recaptchaV2Token) {
      bookOnBehalfData.recaptcha_token_version_2 = recaptchaV2Token
    }

    syncPageRenderedAtMetaForHttpTools()
    const [successBook, bookData] = await httpRequest({
      method: 'POST',
      url: '/api/book_on_behalf',
      data: bookOnBehalfData,
    })

    if (!successBook) {
      const parsed = parseBookValidationError(bookData)
      if (parsed.recaptchaChallengeRequired) {
        dispatch(showRecaptchaChallengeAction())
        return rejectWithValue(parsed)
      }
      return rejectWithValue(parsed)
    }

    if (successBook) {
      const { event: bookedEvent, invitee: bookedInvitee } = bookData

      if (contactsBooking) {
        let trackingSource = source
        if (source === 'crm_contact_card') trackingSource = 'contact_hover_card'
        else if (source === 'crm_contact_page') trackingSource = 'contact_details_page_header'

        trackCRMInviteeBookedEvent({
          eventTypeUuid,
          eventUuid: bookedEvent.uuid,
          inviteeUuid: bookedInvitee.uuid,
          userUuid: currentUserUuid,
          source: trackingSource
        })

        trackCRMFlowStepCompleted({
          eventTypeUuid,
          eventUuid: bookedEvent.uuid,
          inviteeUuid: bookedInvitee.uuid,
          userUuid: currentUserUuid,
          flowLocation: trackingSource,
          flowStep: 'book'
        })
      } else {
        trackInviteeBookedV3({
          user_uuid: currentUserUuid,
          invitee_uuid: bookedInvitee.uuid,
          event_uuid: bookedEvent.uuid,
          event_type_uuid: eventTypeUuid
        }, source, {
          isReschedule: bookingType === 'reschedule',
          isOverrideAvailableTimes: view === 'full_calendar',
        })
      }
    }

    if (invitee.contactUuid !== '') {
      trackScheduleInRealTimeEvent({
        contactUuid: invitee.contactUuid,
        step: 'book',
        source: null,
        track: (config) => {
          trackEvent({
            ...config,
            dependencies: { user_id: currentUserId }
          })
        }
      })
    }

    dispatch(rootApi.util.invalidateTags(['CONTACT']))
    dispatch(closeModalAction())
    dispatch(showSuccess(I18n.t('publisher.booking_modal.booking_success_message')))
    dispatch(setPromoTypeShown('contacts'))

    const { return_to: returnTo, closeRedirectUrl } = parseQuery(window.location.search)
    if (returnTo) {
      extensionSafeRedirect(returnTo || closeRedirectUrl, true, { id: bookData.event.uuid, success: true })
    } else if (onClose) {
      onClose()
    }

    return bookData
  }
)

export const rescheduleEvent = createAsyncThunk(
  'bookModal/rescheduleEvent',
  async (
    { name, location, description, onClose },
    { dispatch, getState, rejectWithValue }
  ) => {
    await dispatch(ensureTelemetryId()).unwrap()

    const state = getState()
    if (state.bookModal.bookingType !== 'reschedule') {
      return rejectWithValue({ error: 'Invalid reschedule state' })
    }

    const rescheduleInviteeUuid = state.bookModal.rescheduleInviteeUuid
    if (!rescheduleInviteeUuid) {
      return rejectWithValue({ error: I18n.t('publisher.booking_modal.errors.required_invitee') })
    }

    const eventType = eventTypeSelector(state)
    const eventTypeUuid = eventTypeUuidSelector(state) || eventType.uuid
    const { view } = calendarStepSelector(state)
    const invitee = primaryInviteeSelector(state)
    const selectedSpot = selectedSpotSelector(state)
    const hasCalendarError = hasCalendarErrorSelector(state)
    const eventFields = inviteeQuestionsBookingSelector(state)
    const rescheduleReason = (state.bookModal.rescheduleReason || '').trim()
    const rescheduleEventUuid = state.bookModal.rescheduleEventUuid

    if (hasCalendarError) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.errors.host_calendar_connection'),
      })
    }

    if (eventFields.some((field) => field.required && !field.value)) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.errors.required_questions'),
      })
    }

    if (!invitee.email || !invitee.name) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.errors.required_invitee'),
      })
    }

    if (!selectedSpot) {
      return rejectWithValue({
        error: I18n.t('publisher.booking_modal.select_time_on_calendar'),
      })
    }

    if (eventType.isCollective) {
      const selectedMembers = selectedMembersSelector(state)
      if (!selectedMembers.length) {
        return rejectWithValue({
          error: I18n.t('publisher.booking_modal.errors.required_host'),
        })
      }
      if (selectedMembers.length > maxHosts) {
        return rejectWithValue({
          error: I18n.t('publisher.booking_modal.errors.allowed_hosts'),
        })
      }
    }

    const trimmed = (invitee.name || '').trim()
    const parts = trimmed.split(/\s+/)
    const firstName = parts[0] || ''
    const lastName = parts.slice(1).join(' ') || ''

    const startTime = selectedSpot.start.format()
    const duration = eventTypeDurationSelector(state)
    const locationConfig = getLocationConfig(location)
    const showRecaptchaChallenge = showRecaptchaChallengeSelector(state)
    const recaptchaV2Token = recaptchaV2TokenSelector(state)
    const stytchTelemetryId = stytchTelemetryIdSelector(state)
    const stytchTelemetryTimestamp = stytchTelemetryTimestampSelector(state)

    const eventGuestsPayload = buildPublisherRescheduleEventGuestsPayload(state.bookModal)
    const selectedMembersForPayload = eventType.isCollective ? selectedMembersSelector(state) : []

    const payload = buildPublisherRescheduleInviteePutBody({
      eventTypeUuid,
      startTime,
      calendarView: view,
      name,
      duration,
      descriptionHtml: description?.html || '',
      locationConfiguration: locationConfig,
      eventGuestsPayload,
      isCollective: !!eventType.isCollective,
      selectedMembers: selectedMembersForPayload,
      rescheduleInviteeUuid,
      rescheduleReason,
      eventFieldsForApi: eventFieldsForBookingApi(eventFields),
      invitee,
      firstName,
      lastName,
      fullName: trimmed,
      timeNotation: timeNotationSelector(state),
      stytchTelemetryId,
      stytchTelemetryTimestampUnix: stytchTelemetryTimestamp ? stytchTelemetryTimestamp.unix() : null,
      showRecaptchaChallenge,
      recaptchaV2Token,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    })

    syncPageRenderedAtMetaForHttpTools()
    const [success, responseBody] = await httpRequest({
      method: 'PUT',
      url: `/api/booking/invitees/${rescheduleInviteeUuid}`,
      data: payload,
    })

    if (!success) {
      const parsed = parseBookValidationError(responseBody || {})
      if (parsed.recaptchaChallengeRequired) {
        dispatch(showRecaptchaChallengeAction())
        return rejectWithValue(parsed)
      }
      return rejectWithValue(parsed)
    }

    dispatch(closeModalAction())
    dispatch(showSuccess(I18n.t('publisher.booking_modal.reschedule_success_message')))

    if (typeof window !== 'undefined' && rescheduleEventUuid) {
      const newEventUuid = responseBody?.event?.uuid
      window.dispatchEvent(
        new CustomEvent(BOOK_MODAL_RESCHEDULE_COMPLETED_EVENT, {
          detail: {
            eventUuid: rescheduleEventUuid,
            ...(newEventUuid ? { newEventUuid } : {}),
          },
        })
      )
    }

    if (onClose) {
      onClose()
    }

    return responseBody
  }
)

const slice = createSlice({
  name: 'bookModal',
  initialState,
  reducers: {
    openModal: (
      state,
      {
        payload: {
          eventTypeId,
          eventTypeUuid,
          eventTypeType,
          eventTypeOwnerType,
          eventTypeOwnerId,
          inviteeName,
          inviteeEmail,
          inviteeTimezone,
          inviteePhone,
          inviteeContactUuid,
          guests,
          source,
          platform,
          bookingType,
          contactsBookingFlow,
          rescheduleInviteeUuid,
          rescheduleEventUuid,
          rescheduleInviteeCharge,
          rescheduleFormerStartIso,
          rescheduleFormerEndIso,
          rescheduleTimezoneLabel,
          rescheduleInviteeEventFields,
          schedulingLinkUuid,
        },
      }
    ) => {
      const guestListFromPayload = guests
        ? guests.split(',').map((e) => ({ id: nanoid(5), email: e.trim() })).filter((row) => row.email).slice(0, 10)
        : []

      return {
        ...state,
        contactsBookingFlow,
        modalVisible: true,
        step: startingStep(source, inviteeEmail, inviteeName),
        mobileStep: MOBILE_STEP_INITIAL,
        eventTypeId,
        eventTypeUuid,
        eventTypeType,
        eventTypeOwnerType,
        eventTypeOwnerId,
        invitees: [{
          name: inviteeName || '',
          email: inviteeEmail || '',
          timezone: inviteeTimezone || '',
          phone: inviteePhone || '',
          primary: true,
          selected: true,
          contactUuid: inviteeContactUuid || '',
        }],
        guests: guestListFromPayload,
        source,
        platform,
        preFilledData: {
          inviteeName,
          inviteeEmail,
          inviteeTimezone,
          inviteePhone,
          guests,
        },
        customFields: [],
        bookingType,
        rescheduleInviteeUuid: rescheduleInviteeUuid || null,
        rescheduleEventUuid: rescheduleEventUuid || null,
        rescheduleReason: '',
        rescheduleInviteeCharge: rescheduleInviteeCharge ?? null,
        rescheduleFormerStartIso: rescheduleFormerStartIso ?? null,
        rescheduleFormerEndIso: rescheduleFormerEndIso ?? null,
        rescheduleTimezoneLabel: rescheduleTimezoneLabel ?? null,
        rescheduleInviteeEventFields: rescheduleInviteeEventFields ?? null,
        rescheduleInitialEventGuestPayload: bookingType === 'reschedule'
          ? guestListFromPayload.map(({ email }) => ({ email }))
          : null,
        rescheduleGuestsFieldTouched: false,
        rescheduleSubmitting: false,
        schedulingLinkUuid: schedulingLinkUuid || null,
      }
    },
    closeModal: () => initialState,
    setStep: (state, { payload: step }) => ({
      ...state,
      step,
    }),
    setMobileStep: (state, { payload: mobileStep }) => ({
      ...state,
      mobileStep,
    }),
    setCalendarView: (state, { payload: view }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        view,
      },
    }),
    setSidebarFormValues: (state, { payload: sidebarFormValues }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        sidebarFormValues,
      },
    }),
    setDraftInviteeName: (state, { payload: name }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        draftInvitee: {
          ...state.calendarStep.draftInvitee,
          name,
        },
      },
    }),
    setDraftInviteeEmail: (state, { payload: email }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        draftInvitee: {
          ...state.calendarStep.draftInvitee,
          email,
        },
      },
    }),
    setDraftInviteeTimezone: (state, { payload: timezone }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        draftInvitee: {
          ...state.calendarStep.draftInvitee,
          timezone,
        },
      },
    }),
    setDraftInviteePhone: (state, { payload: phone }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        draftInvitee: {
          ...state.calendarStep.draftInvitee,
          phone,
        },
      },
    }),
    setDraftInviteeNameError: (state, { payload: nameError }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        draftInvitee: {
          ...state.calendarStep.draftInvitee,
          nameError,
        },
      },
    }),
    setDraftInviteeEmailError: (state, { payload: emailError }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        draftInvitee: {
          ...state.calendarStep.draftInvitee,
          emailError,
        },
      },
    }),
    clearDraftInvitee: (state) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        draftInvitee: {
          name: '',
          email: '',
          timezone: null,
          phone: '',
          nameError: '',
          emailError: '',
        },
      },
    }),
    setEventType: (state, { payload }) => {
      const canonical = payload.eventType ?? payload
      const isMultipleDurationsAvailable = payload.isMultipleDurationsAvailable ?? false

      // For reschedules where the meeting record has already loaded, overlay the
      // booked name and duration (share_override values) over the canonical event type.
      const isRescheduleWithSnapshot = state.bookingType === 'reschedule' && hasScheduledEventData(state.scheduledEvent)
      const { eventType, durationField: patchedDurationField } = isRescheduleWithSnapshot
        ? applyScheduledEventNameDuration(canonical, state.scheduledEvent, state.calendarStep.durationField)
        : { eventType: canonical, durationField: null }

      const effectiveDuration = eventType.duration
      const isInStandardList = DEFAULT_DURATION_OPTIONS.includes(effectiveDuration)
      const useDurationOptions = isMultipleDurationsAvailable && eventType.duration_options?.length > 0
      const isCustomDuration = useDurationOptions
        ? !eventType.duration_options.includes(effectiveDuration)
        : !isInStandardList

      return {
        ...state,
        loadingEventType: false,
        eventType,
        calendarStep: {
          ...state.calendarStep,
          durationField: patchedDurationField ?? {
            ...state.calendarStep.durationField,
            duration: isCustomDuration ? 'custom' : effectiveDuration,
            customDurationValue: isCustomDuration ? effectiveDuration : '',
            customDurationUnit: isCustomDuration ? 'minutes' : state.calendarStep.durationField.customDurationUnit,
          },
        },
      }
    },
    setScheduledEvent: (state, { payload: scheduledEvent }) => {
      const base = { ...state, loadingScheduledEvent: false, scheduledEvent }

      // Retroactive patch: if the canonical event type already loaded before the
      // meeting record arrived, apply the booked name/duration now.
      const shouldRetroactivePatch = state.bookingType === 'reschedule'
        && !state.loadingEventType
        && state.eventType?.id
        && hasScheduledEventData(scheduledEvent)
      if (shouldRetroactivePatch) {
        const { eventType, durationField } = applyScheduledEventNameDuration(
          state.eventType,
          scheduledEvent,
          state.calendarStep.durationField
        )
        return {
          ...base,
          eventType,
          calendarStep: { ...state.calendarStep, durationField },
        }
      }

      return base
    },
    addGuest: (state, { payload }) => ({
      ...state,
      guests: [...state.guests, { id: nanoid(5), email: payload.email }],
      ...(state.bookingType === 'reschedule' ? { rescheduleGuestsFieldTouched: true } : {}),
    }),
    removeGuest: (state, { payload }) => ({
      ...state,
      guests: state.guests.filter((guest) => guest.id !== payload.id),
      ...(state.bookingType === 'reschedule' ? { rescheduleGuestsFieldTouched: true } : {}),
    }),
    updateGuest: (state, { payload }) => ({
      ...state,
      guests: state.guests.map((guest) => ({
        id: guest.id,
        email: guest.id === payload.id ? payload.email : guest.email,
      })),
      ...(state.bookingType === 'reschedule' ? { rescheduleGuestsFieldTouched: true } : {}),
    }),
    setQuestionFormValues: (state, { payload }) => {
      let errorMsg = state.bookErrorMsg
      if (state.bookErrorMsg === 'This event type contains required questions') {
        if (payload && !payload.some((field) => field.required && !field.value)) {
          errorMsg = ''
        }
      }
      return {
        ...state,
        bookErrorMsg: errorMsg,
        calendarStep: {
          ...state.calendarStep,
          questionFormValues: payload
        },
      }
    },
    setPendingTrackInviteeSelected: (
      state,
      { payload: pendingTrackInviteeSelected }
    ) => ({
      ...state,
      pendingTrackInviteeSelected,
    }),
    setInviteeSelectedTracked: (state) => ({
      ...state,
      inviteeSelectedTracked: true,
    }),
    setCurrentTime: (state, { payload: currentTime }) => ({
      ...state,
      currentTime,
    }),
    selectHost: (state, { payload: host }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        timezone: host.selectedTimezone,
        hostsField: {
          ...state.calendarStep.hostsField,
          selectedMembers: state.calendarStep.hostsField.selectedMembers.map(
            (member) => ({ ...member, selectedHost: member.id === host.id })
          ),
        },
      },
    }),
    selectInvitee: (state, { payload: invitee }) => ({
      ...state,
      invitees: state.invitees.map((i) => ({
        ...i,
        selected: i.id === invitee.id,
      })),
      calendarStep: {
        ...state.calendarStep,
        timezone: invitee.timezone,
      },
    }),
    clearInvitee: (state) => {
      const selectedHost = state.calendarStep.hostsField.selectedMembers.find((m) => m.selectedHost)
      return {
        ...state,
        invitees: [
          emptyInvitee,
        ],
        guests: [],
        ...(state.bookingType === 'reschedule' ? { rescheduleGuestsFieldTouched: true } : {}),
        calendarStep: {
          ...state.calendarStep,
          timezone: selectedHost?.selectedTimezone ? selectedHost.selectedTimezone : state.calendarStep.timezone,
        }
      }
    },
    changeTimezone: (state, { payload: timezone }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        timezone,
      },
    }),
    changeHostTimezone: (state, { payload: { host, timezone } }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        timezone:
          host.selectedHost
            && host.selectedTimezone === state.calendarStep.timezone
            ? timezone
            : state.calendarStep.timezone,
        hostsField: {
          ...state.calendarStep.hostsField,
          selectedMembers: state.calendarStep.hostsField.selectedMembers.map(
            (member) => ({
              ...member,
              selectedTimezone:
                member.id === host.id ? timezone : member.selectedTimezone,
            })
          ),
        },
      },
    }),
    changeInviteeTimezone: (state, { payload: { invitee, timezone } }) => {
      const selectedHost = state.calendarStep.hostsField.selectedMembers.find((m) => m.selectedHost)
      const selectedTimezone = state.calendarStep.timezone
      return {
        ...state,
        invitees: state.invitees.map((i) => ({
          ...i,
          timezone: i.id === invitee.id ? timezone : i.timezone,
        })),
        calendarStep: {
          ...state.calendarStep,
          timezone: invitee.selected && selectedTimezone !== selectedHost?.selectedTimezone && selectedTimezone === invitee.timezone
            ? timezone
            : state.calendarStep.timezone,
        },
      }
    },
    setDatesRange: (state, { payload: { rangeStart, rangeEnd } }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        dateRangeStart: rangeStart,
        rangeStart: rangeStart.format('YYYY-MM-DD'),
        rangeEnd: rangeEnd.format('YYYY-MM-DD'),
      },
    }),
    setFetchedEventsStartRange: (state, { payload: { rangeStart } }) => ({
      ...state,
      calendarStep: {
        ...state.calendarStep,
        fetchedEventsStartRange: rangeStart,
      },
    }),
    selectNewSpot: (state, { payload: selectedSpot }) => ({
      ...state,
      calendarStep: { ...state.calendarStep, selectedSpot },
    }),
    removeSelectedSpot: (state, { payload: { start } }) => (state.calendarStep.selectedSpot
      && state.calendarStep.selectedSpot.start.isSame(start)
      ? {
        ...state,
        calendarStep: { ...state.calendarStep, selectedSpot: null },
      }
      : state),
    updateDurationField: (state, { payload: { field, value, currentUser } }) => (
      applyDurationFieldChange(state, {
        ...state.calendarStep.durationField,
        [field]: value,
      }, currentUser)
    ),
    commitDurationField: (state, { payload: { durationField, currentUser } }) => (
      applyDurationFieldChange(state, durationField, currentUser)
    ),
    changeCalendarMonth: (state, { payload }) => ({
      ...state,
      calendarList: {
        ...state.calendarList,
        monthShift: state.calendarList.monthShift + payload
      }
    }),
    // dayToSelect comes from booking-kit Calendar or mobile week strip (Moment / Luxon / ISO / { id, date }).
    selectCalendarDay: (state, { payload }) => ({
      ...state,
      calendarList: {
        ...state.calendarList,
        spotAutofocus: Boolean(payload?.isFromKeyboard),
        calendar: {
          ...state.calendarList.calendar,
          day: normalizeSelectedCalendarDay(payload?.dayToSelect),
        }
      }
    }),
    setToday: (state, { payload }) => ({
      ...state,
      calendarList: {
        ...state.calendarList,
        today: payload?.today,
      }
    }),
    trackStytchTelemetryId: (state, { payload }) => ({
      ...state,
      stytchTelemetryId: payload.telemetryId,
      stytchTelemetryTimestamp: payload.timestamp || null
    }),
    showRecaptchaChallenge: (state) => ({
      ...state,
      recaptcha: {
        ...state.recaptcha,
        showChallenge: true,
        v2Token: null,
      },
    }),
    setRecaptchaV2Token: (state, { payload }) => ({
      ...state,
      recaptcha: {
        ...state.recaptcha,
        v2Token: payload,
      },
    }),
    setRescheduleReason: (state, { payload }) => ({
      ...state,
      rescheduleReason: payload,
    }),
    setPrimaryInviteeContact: (state, { payload: { name, email } }) => ({
      ...state,
      invitees: state.invitees.map((inv) => (
        inv.primary
          ? {
            ...inv,
            ...(name !== undefined ? { name } : {}),
            ...(email !== undefined ? { email } : {}),
          }
          : inv
      )),
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(setPrimaryInvitee.fulfilled, (state, { payload }) => ({
      ...state,
      bookErrorMsg: state.bookErrorMsg === BookErrorMessages.requiredInvitee ? '' : state.bookErrorMsg,
      invitees: [
        {
          ...payload,
          primary: true,
          timezone: getInviteeTimezone(payload, state),
        },
        ...state.invitees.filter((invitee) => !invitee.primary),
      ],
    }))
    builder.addCase(fetchBookEventType.pending, (state) => ({
      ...state,
      loadingEventType: true,
      loadingEventTypeMembers: true,
      calendarStep: { ...state.calendarStep, loadingCalendarData: true, sidebarFormValues: null },
    }))
    builder.addCase(fetchBookEventType.rejected, (state) => ({
      ...state,
      loadingEventType: false,
      loadingEventTypeMembers: false,
      calendarStep: { ...state.calendarStep, loadingCalendarData: false },
    }))
    builder.addCase(
      fetchBookEventType.fulfilled,
      (
        state,
        { payload: { eventType: canonicalEventType, eventTypeUuid, selectedMembers, selectedHostIndex } }
      ) => {
        // Re-apply the reschedule name/duration patch here because this handler fires
        // AFTER member fetches and always overwrites state.eventType — without this,
        // fetchBookEventType.fulfilled kills any patch applied by setEventType or
        // the setScheduledEvent retroactive patch.
        const isRescheduleWithSnapshot = state.bookingType === 'reschedule' && hasScheduledEventData(state.scheduledEvent)
        const { eventType, durationField: patchedDurationField } = isRescheduleWithSnapshot
          ? applyScheduledEventNameDuration(canonicalEventType, state.scheduledEvent, state.calendarStep.durationField)
          : { eventType: canonicalEventType, durationField: null }

        const nextQuestionFormValues = state.bookingType === 'reschedule'
          ? resolveRescheduleQuestionFormValues(eventType, state)
          : state.calendarStep.questionFormValues

        return {
          ...state,
          loadingEventType: false,
          loadingEventTypeMembers: false,
          eventType,
          eventTypeUuid: eventTypeUuid || state.eventTypeUuid,
          invitees: state.invitees.map((i) => ({
            ...i,
            timezone:
              i.selected && !i.timezone
                ? selectedMembers[selectedHostIndex].user_timezone
                : i.timezone,
          })),
          calendarStep: {
            ...state.calendarStep,
            loadingCalendarData: false,
            timezone: selectedMembers[selectedHostIndex].user_timezone,
            hostsField: {
              ...state.calendarStep.hostsField,
              colorIndexes: state.calendarStep.hostsField.colorIndexes.slice(
                selectedMembers.length
              ),
              selectedMembers: selectedMembers.map((member, idx) => ({
                ...member,
                isOrganizer: member.location_organizer,
                selectedHost: idx === selectedHostIndex,
                selectedTimezone: member.user_timezone,
                colorIndex: state.calendarStep.hostsField.colorIndexes[idx] || 0,
              })),
            },
            durationField: patchedDurationField ?? state.calendarStep.durationField,
            customFields: eventType.custom_fields,
            questionFormValues: nextQuestionFormValues,
          },
        }
      }
    )
    builder.addCase(fetchScheduledEventData.pending, (state) => ({
      ...state,
      loadingScheduledEvent: true,
      calendarStep: { ...state.calendarStep, loadingCalendarData: true, sidebarFormValues: null },
    }))
    builder.addCase(fetchScheduledEventData.rejected, (state) => ({
      ...state,
      loadingScheduledEvent: false,
      calendarStep: { ...state.calendarStep, loadingCalendarData: false },
    }))
    builder.addCase(
      fetchScheduledEventData.fulfilled,
      (state, { payload: { scheduledEvent, selectedMembers, selectedHostIndex } }) => {
        // Re-apply the reschedule name/duration patch here. The original code unconditionally
        // resets durationField.duration to 30, which fires last and wipes any patch from
        // setScheduledEvent or fetchBookEventType.fulfilled. For reschedules, either apply
        // the deviation patch (when eventType is already loaded) or keep the current durationField.
        const isReschedule = state.bookingType === 'reschedule'
        const canPatch = isReschedule && hasScheduledEventData(scheduledEvent) && state.eventType?.id
        const { eventType: patchedEventType, durationField: patchedDurationField } = canPatch
          ? applyScheduledEventNameDuration(state.eventType, scheduledEvent, state.calendarStep.durationField)
          : { eventType: state.eventType, durationField: null }

        const effectiveEventType = canPatch ? patchedEventType : state.eventType
        const questionFormValues = isReschedule
          ? resolveRescheduleQuestionFormValues(effectiveEventType, state, scheduledEvent)
          : state.calendarStep.questionFormValues

        return {
          ...state,
          scheduledEvent,
          loadingScheduledEvent: false,
          loadingEventTypeMembers: false,
          ...(canPatch && { eventType: patchedEventType }),
          invitees: state.invitees.map((i) => ({
            ...i,
            timezone:
              i.selected && !i.timezone
                ? selectedMembers[selectedHostIndex]?.user_timezone
                : i.timezone,
          })),
          calendarStep: {
            ...state.calendarStep,
            fetchedEventsStartRange: moment('1900', 'YYYY').format('YYY-MM-DD'),
            loadingCalendarData: false,
            timezone: selectedMembers[selectedHostIndex]?.user_timezone ?? state.calendarStep.timezone,
            hostsField: {
              ...state.calendarStep.hostsField,
              colorIndexes: state.calendarStep.hostsField.colorIndexes.slice(
                selectedMembers.length
              ),
              // Do not override `user_id` to `member.id`. `member.id` is the event-type
              // membership row id, not the user id. Spreading `...member` already carries
              // the correct `user_id`. Stomping it makes `getSelectedMemberEvents`
              // mis-classify the host's own meetings as `hostType: 'secondary'`, which
              // flips overlapping spots to `available: false` and paints an "Unavailable"
              // overlay over them in the publisher reschedule + override-availability flow.
              selectedMembers: selectedMembers.map((member, idx) => ({
                ...member,
                isOrganizer: member.location_organizer,
                selectedHost: idx === selectedHostIndex,
                selectedTimezone: member.user_timezone,
                colorIndex: state.calendarStep.hostsField.colorIndexes[idx] || 0,
              })),
            },
            durationField: patchedDurationField ?? (
              isReschedule
                ? state.calendarStep.durationField
                : { ...state.calendarStep.durationField, duration: 30, customDurationValue: '' }
            ),
            questionFormValues,
          },
        }
      }
    )
    builder.addCase(updateMembersEventData.pending, (state) => ({
      ...state,
      calendarStep: { ...state.calendarStep, loadingCalendarData: true },
    }))
    builder.addCase(updateMembersEventData.rejected, (state) => ({
      ...state,
      calendarStep: { ...state.calendarStep, loadingCalendarData: false },
    }))
    builder.addCase(
      updateMembersEventData.fulfilled,
      (state, { payload: membersEventData }) => ({
        ...state,
        calendarStep: {
          ...state.calendarStep,
          loadingCalendarData: false,
          hostsField: {
            ...state.calendarStep.hostsField,
            selectedMembers: state.calendarStep.hostsField.selectedMembers.map(
              (member) => {
                const memberEventData = membersEventData.find(
                  (m) => m.userId === member.user_uuid
                )
                if (memberEventData) {
                  return { ...member, eventData: memberEventData.eventData, calendarError: memberEventData.calendarError }
                }
                return { ...member }
              }
            ),
          },
        },
      })
    )
    builder.addCase(bookEvent.rejected, (state, { payload }) => ({
      ...state,
      bookErrorMsg: payload?.recaptchaChallengeRequired ? '' : (payload?.error || DEFAULT_BOOKING_ERROR_MESSAGE),
      stytchBlockContext: payload?.stytchBlockContext ?? null,
      ...(payload && !payload.recaptchaChallengeRequired ? {
        recaptcha: { ...state.recaptcha, showChallenge: false, v2Token: null }
      } : {})
    }))
    builder.addCase(bookEvent.fulfilled, (state) => ({
      ...state,
      bookErrorMsg: '',
      stytchBlockContext: null,
    }))
    builder.addCase(bookEvent.pending, (state) => ({
      ...state,
      bookErrorMsg: '',
      stytchBlockContext: null
    }))
    builder.addCase(bookAdhocEvent.rejected, (state, { payload }) => ({
      ...state,
      bookErrorMsg: payload?.recaptchaChallengeRequired ? '' : (payload?.error || DEFAULT_BOOKING_ERROR_MESSAGE),
      stytchBlockContext: payload?.stytchBlockContext ?? null,
      ...(payload && !payload.recaptchaChallengeRequired ? {
        recaptcha: { ...state.recaptcha, showChallenge: false, v2Token: null }
      } : {})
    }))
    builder.addCase(bookAdhocEvent.fulfilled, (state) => ({
      ...state,
      bookErrorMsg: '',
      stytchBlockContext: null,
    }))
    builder.addCase(bookAdhocEvent.pending, (state) => ({
      ...state,
      bookErrorMsg: '',
      stytchBlockContext: null
    }))
    builder.addCase(rescheduleEvent.pending, (state) => ({
      ...state,
      rescheduleSubmitting: true,
      bookErrorMsg: '',
      stytchBlockContext: null,
    }))
    builder.addCase(rescheduleEvent.fulfilled, (state) => ({
      ...state,
      rescheduleSubmitting: false,
      bookErrorMsg: '',
      stytchBlockContext: null,
    }))
    builder.addCase(rescheduleEvent.rejected, (state, { payload }) => ({
      ...state,
      rescheduleSubmitting: false,
      bookErrorMsg: payload?.recaptchaChallengeRequired ? '' : (payload?.error || DEFAULT_BOOKING_ERROR_MESSAGE),
      stytchBlockContext: payload?.stytchBlockContext ?? null,
      ...(payload && !payload.recaptchaChallengeRequired ? {
        recaptcha: { ...state.recaptcha, showChallenge: false, v2Token: null }
      } : {}),
    }))
    builder.addCase(fetchEventTypeAvailability.pending, (state) => ({
      ...state,
      calendarList: { ...state.calendarList, loadingCalendarData: true },
    }))
    builder.addCase(fetchEventTypeAvailability.rejected, (state) => ({
      ...state,
      calendarList: { ...state.calendarList, loadingCalendarData: false },
    }))
    builder.addCase(
      fetchEventTypeAvailability.fulfilled,
      (state, { payload: { availability: { days }, month } }) => ({
        ...state,
        calendarList: {
          ...state.calendarList,
          loadingCalendarData: false,
          calendars: {
            ...state.calendarList.calendars,
            [month.startOf('month').format('YYYY-MM-DD')]: { days }
          }
        },
      })
    )
    builder.addCase(removeHost.pending, (state) => ({
      ...state,
      calendarList: { ...state.calendarList, loadingCalendarData: true, calendars: {} },
    }))
    builder.addCase(removeHost.fulfilled, (state, { payload: host }) => ({
      ...state,
      bookErrorMsg: state.bookErrorMsg === BookErrorMessages.allowedUpTo30hosts
        && state.calendarStep.hostsField.selectedMembers.length - 1 <= maxHosts
        ? ''
        : state.bookErrorMsg,
      calendarStep: {
        ...state.calendarStep,
        hostsField: {
          ...state.calendarStep.hostsField,
          colorIndexes: [...state.calendarStep.hostsField.colorIndexes, host.colorIndex].sort(),
          selectedMembers: state.calendarStep.hostsField.selectedMembers.filter(
            (member) => member.id !== host.id
          ),
        },
      },
    }))
  },
})

setEventTypeAction = slice.actions.setEventType
setScheduledEventAction = slice.actions.setScheduledEvent
setPendingTrackInviteeSelectedAction = slice.actions.setPendingTrackInviteeSelected
setInviteeSelectedTrackedAction = slice.actions.setInviteeSelectedTracked
closeModalAction = slice.actions.closeModal
setTelemetryIdAction = slice.actions.trackStytchTelemetryId
showRecaptchaChallengeAction = slice.actions.showRecaptchaChallenge

export const {
  reducer,
  actions: {
    openModal,
    closeModal,
    setRescheduleReason,
    setPrimaryInviteeContact,
    setMobileStep,
    // setStep,
    setSidebarFormValues,
    setCalendarView,
    setCurrentTime,
    selectHost,
    selectInvitee,
    setToday,
    changeTimezone,
    // changeHostTimezone,
    setQuestionFormValues,
    setDatesRange,
    setFetchedEventsStartRange,
    selectNewSpot,
    removeSelectedSpot,
    addGuest,
    removeGuest,
    updateGuest,
    updateDurationField,
    commitDurationField,
    changeCalendarMonth,
    selectCalendarDay,
    setDraftInviteeName,
    setDraftInviteeEmail,
    setDraftInviteeTimezone,
    setDraftInviteePhone,
    setDraftInviteeNameError,
    setDraftInviteeEmailError,
    clearDraftInvitee,
    clearInvitee,
    changeInviteeTimezone,
    setRecaptchaV2Token,
  },
} = slice
